import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Users, Calendar, Megaphone, BarChart2, ShieldAlert, Search, 
  UserPlus, X, Plus, Loader, Network, Trash2, Edit3, Image as ImageIcon, 
  CheckCircle, Clock, ArrowRight, ExternalLink 
} from 'lucide-react';
import API_URL from '../../config';
import './Events.css';
import './EventsPrototype.css';

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
}

export default function EventAdminDashboard() {
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [user, setUser] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Participants state
  const [participants, setParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantTotal, setParticipantTotal] = useState(0);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  // Connections state (People-to-People)
  const [connections, setConnections] = useState([]);
  const [connectionsSearch, setConnectionsSearch] = useState('');
  const [connectionsStatusFilter, setConnectionsStatusFilter] = useState('all');
  const [totalAcceptedConns, setTotalAcceptedConns] = useState(0);
  const [totalPendingConns, setTotalPendingConns] = useState(0);
  const [loadingConnections, setLoadingConnections] = useState(false);

  // Sub-events state
  const [activities, setActivities] = useState([]);
  const [showCreateActModal, setShowCreateActModal] = useState(false);
  const [newAct, setNewAct] = useState({
    name: '',
    category: 'Hackathon',
    description: '',
    rules: '',
    date: '',
    start_time: '10:00',
    end_time: '18:00',
    venue: '',
    team_size: 4,
    registration_limit: 100
  });
  const [creatingAct, setCreatingAct] = useState(false);

  // Edit Event / Banner Modal state
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [editEventForm, setEditEventForm] = useState({
    name: '',
    description: '',
    banner: '',
    venue: '',
    start_date: '',
    end_date: ''
  });
  const [savingEvent, setSavingEvent] = useState(false);

  // Assign admin state
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [adminSearching, setAdminSearching] = useState(false);
  const [assignMessage, setAssignMessage] = useState('');
  const adminSearchRef = useRef(null);

  // Announcement state
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);

  // Strict Light Theme enforcement for all event pages
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  // Authorization and initial load
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('vjconnect_user'));
    if (!storedUser || !storedUser.email) {
      setIsAuthorized(false);
      return;
    }
    setUser(storedUser);

    fetch(`${API_URL}/events-admin/my-events?email=${storedUser.email}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.events && data.events.some(e => e.slug === slug || storedUser.role === 'admin')) {
          setIsAuthorized(true);
          const ev = data.events.find(e => e.slug === slug);
          if (ev) {
            setEventData(ev);
            setEditEventForm({
              name: ev.name || '',
              description: ev.description || '',
              banner: ev.banner || ev.poster || '',
              venue: ev.venue || '',
              start_date: ev.start_date || '',
              end_date: ev.end_date || ''
            });
          }
        } else if (storedUser.role === 'admin') {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      })
      .catch(() => setIsAuthorized(false));
  }, [slug]);

  // Fetch dashboard stats
  const fetchDashboardStats = useCallback(() => {
    if (!user || !eventData) return;
    fetch(`${API_URL}/events-admin/${eventData.id}/dashboard?admin_email=${user.email}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setDashboardStats(data);
        }
      })
      .catch(console.error);
  }, [user, eventData]);

  // Fetch connections (People-to-People list)
  const fetchConnections = useCallback(() => {
    if (!user || !eventData) return;
    setLoadingConnections(true);
    const params = new URLSearchParams({ admin_email: user.email });
    if (connectionsStatusFilter !== 'all') params.append('status', connectionsStatusFilter);
    if (connectionsSearch) params.append('search', connectionsSearch);

    fetch(`${API_URL}/events-admin/${eventData.id}/connections?${params.toString()}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setConnections(data.connections || []);
          setTotalAcceptedConns(data.total_accepted || 0);
          setTotalPendingConns(data.total_pending || 0);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingConnections(false));
  }, [user, eventData, connectionsStatusFilter, connectionsSearch]);

  // Fetch sub-events (activities)
  const fetchActivities = useCallback(() => {
    if (!eventData) return;
    fetch(`${API_URL}/events/${eventData.slug}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.event && data.event.activities) {
          setActivities(data.event.activities);
        }
      })
      .catch(console.error);
  }, [eventData]);

  // Fetch participants
  const fetchParticipants = useCallback(() => {
    if (!user || !eventData) return;
    setLoadingParticipants(true);
    const search = participantSearch ? `&search=${encodeURIComponent(participantSearch)}` : '';
    fetch(`${API_URL}/events-admin/${eventData.id}/participants?admin_email=${user.email}${search}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setParticipants(data.participants || []);
          setParticipantTotal(data.total || 0);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingParticipants(false));
  }, [user, eventData, participantSearch]);

  // Initial tab loads
  useEffect(() => {
    if (!isAuthorized || !user || !eventData) return;
    fetchDashboardStats();
    fetchActivities();
  }, [isAuthorized, user, eventData, fetchDashboardStats, fetchActivities]);

  useEffect(() => {
    if (!isAuthorized || !user || !eventData) return;
    if (activeTab === 'participants') fetchParticipants();
    if (activeTab === 'connections') fetchConnections();
    if (activeTab === 'subevents') fetchActivities();
  }, [activeTab, isAuthorized, user, eventData, fetchParticipants, fetchConnections, fetchActivities]);

  // LIVE POLLING: Auto-refresh stats and active tab data every 6 seconds
  useEffect(() => {
    if (!isAuthorized || !user || !eventData) return;
    const interval = setInterval(() => {
      fetchDashboardStats();
      if (activeTab === 'connections') fetchConnections();
      if (activeTab === 'participants') fetchParticipants();
      if (activeTab === 'subevents') fetchActivities();
    }, 6000);

    return () => clearInterval(interval);
  }, [isAuthorized, user, eventData, activeTab, fetchDashboardStats, fetchConnections, fetchParticipants, fetchActivities]);

  // Admin search debounce
  useEffect(() => {
    if (adminSearchQuery.length < 2) { setAdminSearchResults([]); return; }
    setAdminSearching(true);
    const timer = setTimeout(() => {
      fetch(`${API_URL}/student/search?q=${encodeURIComponent(adminSearchQuery)}`)
        .then(res => res.json())
        .then(data => { setAdminSearchResults(data.users || []); setAdminSearching(false); })
        .catch(() => setAdminSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [adminSearchQuery]);

  const handleAssignAdmin = async (targetEmail) => {
    if (!eventData) return;
    try {
      const res = await fetch(`${API_URL}/events-admin/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_id: eventData.id, user_email: targetEmail, admin_email: user.email, assigned_by: user.email })
      });
      const data = await res.json();
      if (data.success) {
        setAssignMessage(`✅ ${targetEmail} assigned as admin!`);
        setAdminSearchQuery('');
        setAdminSearchResults([]);
        setTimeout(() => setAssignMessage(''), 3000);
      } else {
        setAssignMessage(`❌ ${data.error || 'Failed to assign'}`);
      }
    } catch (e) {
      setAssignMessage('❌ Error assigning admin');
    }
  };

  const handleCreateActivity = async () => {
    if (!newAct.name.trim() || !eventData) return;
    setCreatingAct(true);
    try {
      const res = await fetch(`${API_URL}/events-admin/${eventData.id}/activities/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newAct, admin_email: user.email })
      });
      const data = await res.json();
      if (data.success) {
        alert(`Sub-event "${newAct.name}" created successfully!`);
        setShowCreateActModal(false);
        setNewAct({
          name: '',
          category: 'Hackathon',
          description: '',
          rules: '',
          date: '',
          start_time: '10:00',
          end_time: '18:00',
          venue: '',
          team_size: 4,
          registration_limit: 100
        });
        fetchActivities();
        fetchDashboardStats();
      } else {
        alert(data.error || 'Failed to create sub-event');
      }
    } catch (e) {
      alert('Error creating sub-event');
    } finally {
      setCreatingAct(false);
    }
  };

  const handleDeleteActivity = async (actId, actName) => {
    if (!window.confirm(`Delete sub-event "${actName}"? This will remove attendee registrations for this sub-event.`)) return;
    try {
      const res = await fetch(`${API_URL}/events-admin/${eventData.id}/activities/${actId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_email: user.email })
      });
      const data = await res.json();
      if (data.success) {
        alert('Sub-event deleted successfully');
        fetchActivities();
        fetchDashboardStats();
      } else {
        alert(data.error || 'Failed to delete activity');
      }
    } catch (e) {
      alert('Error deleting activity');
    }
  };

  const handleEditBannerFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditEventForm(prev => ({ ...prev, banner: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveEventDetails = async () => {
    if (!editEventForm.name.trim() || !eventData) return;
    setSavingEvent(true);
    try {
      const res = await fetch(`${API_URL}/events-admin/${eventData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editEventForm,
          admin_email: user.email
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('Event details updated successfully!');
        setEventData(data.event);
        setShowEditEventModal(false);
      } else {
        alert(data.error || 'Failed to update event');
      }
    } catch (e) {
      alert('Error updating event');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleSendAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementMessage.trim() || !eventData) return;
    setSendingAnnouncement(true);
    try {
      const res = await fetch(`${API_URL}/events-admin/${eventData.id}/announcements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: announcementTitle, message: announcementMessage, priority: 'normal', created_by: user.email, admin_email: user.email })
      });
      const data = await res.json();
      if (data.success) {
        alert('Announcement sent to all participants!');
        setAnnouncementTitle('');
        setAnnouncementMessage('');
      }
    } catch (e) { alert('Error sending announcement'); }
    finally { setSendingAnnouncement(false); }
  };

  if (isAuthorized === null) {
    return (
      <div className="events-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <Loader size={36} className="ev-spin" style={{ color: 'var(--ev-purple)' }} />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="events-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center', background: 'var(--glass-bg)', padding: '3rem', borderRadius: '24px', border: '1px solid var(--glass-border)', maxWidth: '450px' }}>
          <ShieldAlert size={48} color="#ef4444" style={{ marginBottom: '1rem' }} />
          <h2 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-main)' }}>Access Denied</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>You do not have permission to access the Event Admin Dashboard for <b>{slug}</b>.</p>
          <Link to={`/events/${slug}`} className="ev-btn-primary">Back to Event</Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview & Stats', icon: <BarChart2 size={18} /> },
    { id: 'connections', label: `Connections (${totalAcceptedConns})`, icon: <Network size={18} /> },
    { id: 'subevents', label: `Sub-Events (${activities.length})`, icon: <Calendar size={18} /> },
    { id: 'participants', label: `Participants (${participantTotal || dashboardStats?.total_participants || 0})`, icon: <Users size={18} /> },
    { id: 'announcements', label: 'Broadcast', icon: <Megaphone size={18} /> },
  ];

  return (
    <div className="events-page" data-theme="light" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Top Header */}
      <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--glass-border)', background: 'var(--glass-bg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>
            {eventData?.name || slug} <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 500 }}>Admin Panel</span>
          </h1>
          {/* Live Indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', color: '#22c55e', fontWeight: 700 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'pulse-dot 1.5s infinite' }}></span>
            Live Updates
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={() => setShowEditEventModal(true)} 
            className="ev-btn-secondary" 
            style={{ padding: '0.45rem 0.9rem', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            <Edit3 size={14} /> Edit Event & Cover
          </button>
          <Link to={`/events/${slug}`} className="ev-btn-secondary" style={{ padding: '0.45rem 0.9rem', fontSize: '0.82rem', textDecoration: 'none' }}>
            🌐 View Event Page
          </Link>
          <Link to={`/events/${slug}/networking`} className="ev-btn-primary" style={{ padding: '0.45rem 0.9rem', fontSize: '0.82rem', textDecoration: 'none' }}>
            🤝 Networking Hub
          </Link>
          <button 
            className="ev-btn-secondary" 
            style={{ padding: '0.45rem 0.85rem', fontSize: '0.82rem', background: 'rgba(239,68,68,0.15)', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}
            onClick={async () => {
              if (!window.confirm(`Are you sure you want to permanently delete event "${slug}"? All sub-events and networking data will be permanently erased.`)) return;
              try {
                if (!eventData) return alert('Event not found');
                const res = await fetch(`${API_URL}/events-admin/${eventData.id}?admin_email=${user.email}`, { method: 'DELETE' });
                const d = await res.json();
                if (d.success) {
                  alert('Event deleted successfully!');
                  window.location.href = '/events';
                } else alert(d.error || 'Failed to delete event');
              } catch(e) { alert('Error deleting event'); }
            }}
          >
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>
      
      {/* Body layout */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar Nav */}
        <div style={{ width: '240px', borderRight: '1px solid var(--glass-border)', background: 'var(--glass-bg)', padding: '1rem 0', flexShrink: 0 }}>
          {tabs.map(tab => (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.8rem 1.25rem',
                background: activeTab === tab.id ? 'rgba(139,92,246,0.12)' : 'transparent',
                color: activeTab === tab.id ? 'var(--ev-purple)' : 'var(--text-secondary)',
                border: 'none', borderRight: activeTab === tab.id ? '3px solid var(--ev-purple)' : '3px solid transparent',
                textAlign: 'left', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, transition: 'all 0.2s'
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
        
        {/* Main Content Pane */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
          
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Dashboard Overview</h2>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Auto-refreshing every 6s • Connected with link: /events/{slug}
                </span>
              </div>

              {/* Event Cover Banner Preview (if set) */}
              {eventData?.banner && (
                <div style={{ position: 'relative', height: '140px', borderRadius: 16, overflow: 'hidden', marginBottom: '1.5rem', border: '1px solid var(--glass-border)' }}>
                  <img src={eventData.banner} alt={eventData.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent 60%)', display: 'flex', alignItems: 'flex-end', padding: '1rem 1.5rem' }}>
                    <div style={{ color: 'white', fontWeight: 800, fontSize: '1.2rem' }}>{eventData.name}</div>
                  </div>
                </div>
              )}

              {/* Live Stats Cards Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                {/* Total Participants Joined */}
                <div 
                  className="ev-stat-card" 
                  onClick={() => setActiveTab('participants')}
                  style={{ cursor: 'pointer', padding: '1.25rem', textAlign: 'left', transition: 'all 0.2s' }}
                >
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Users size={15} color="var(--ev-purple)" /> Total Participants
                  </div>
                  <div style={{ fontSize: '2.4rem', fontWeight: 900, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                    {dashboardStats?.total_participants ?? '...'}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span>Joined from event link</span>
                  </div>
                </div>

                {/* Accepted Connections Made */}
                <div 
                  className="ev-stat-card" 
                  onClick={() => setActiveTab('connections')}
                  style={{ cursor: 'pointer', padding: '1.25rem', textAlign: 'left', transition: 'all 0.2s' }}
                >
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Network size={15} color="#22c55e" /> Connections Made
                  </div>
                  <div style={{ fontSize: '2.4rem', fontWeight: 900, color: '#22c55e', margin: '0.4rem 0' }}>
                    {dashboardStats?.connection_count ?? '...'}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Mutual accepted connections
                  </div>
                </div>

                {/* Pending Requests */}
                <div 
                  className="ev-stat-card" 
                  onClick={() => { setActiveTab('connections'); setConnectionsStatusFilter('pending'); }}
                  style={{ cursor: 'pointer', padding: '1.25rem', textAlign: 'left', transition: 'all 0.2s' }}
                >
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Clock size={15} color="var(--ev-orange)" /> Pending Requests
                  </div>
                  <div style={{ fontSize: '2.4rem', fontWeight: 900, color: 'var(--ev-orange)', margin: '0.4rem 0' }}>
                    {dashboardStats?.pending_connection_count ?? '0'}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Awaiting peer acceptance
                  </div>
                </div>

                {/* Profile Completion Avg */}
                <div className="ev-stat-card" style={{ padding: '1.25rem', textAlign: 'left' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <CheckCircle size={15} color="var(--ev-purple)" /> Avg Profile Completion
                  </div>
                  <div style={{ fontSize: '2.4rem', fontWeight: 900, color: 'var(--ev-purple)', margin: '0.4rem 0' }}>
                    {dashboardStats?.profile_completion_avg ?? '...'}%
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Across registered attendees
                  </div>
                </div>
              </div>

              {/* Sub-Events Overview Quick Bar */}
              <div style={{ background: 'var(--glass-bg)', borderRadius: 16, border: '1px solid var(--glass-border)', padding: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Calendar size={18} color="var(--ev-purple)" /> Sub-Events Activity Summary
                  </h3>
                  <button onClick={() => setActiveTab('subevents')} className="ev-btn-secondary" style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}>
                    Manage Sub-Events →
                  </button>
                </div>
                {dashboardStats?.activity_breakdown?.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
                    {dashboardStats.activity_breakdown.map((act, i) => (
                      <div key={i} style={{ background: 'rgba(255,255,255,0.03)', padding: '0.85rem 1rem', borderRadius: 12, border: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{act.activity_name}</span>
                        <span style={{ color: 'var(--ev-purple)', fontWeight: 800, fontSize: '0.9rem' }}>{act.count} registered</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', margin: 0 }}>No sub-events configured yet.</p>
                )}
              </div>

              {/* Assign Event Admin Section */}
              <div style={{ padding: '1.5rem', background: 'var(--glass-bg)', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
                  <UserPlus size={18} /> Assign Co-Admin for this Event
                </h3>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 1rem 0' }}>
                  Grant event management permissions to faculty or student organizer emails.
                </p>
                <div style={{ position: 'relative' }} ref={adminSearchRef}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <Search size={16} style={{ color: 'var(--text-muted)' }} />
                    <input
                      className="ev-input"
                      value={adminSearchQuery}
                      onChange={e => setAdminSearchQuery(e.target.value)}
                      placeholder="Search by name, email, or roll number..."
                      style={{ flex: 1 }}
                    />
                  </div>
                  {adminSearchQuery.length >= 2 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#181832', backdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)', borderRadius: '12px', marginTop: '4px', maxHeight: '250px', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                      {adminSearching ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Searching...</div>
                      ) : adminSearchResults.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No users found</div>
                      ) : adminSearchResults.map(u => (
                        <div key={u.email} style={{ padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--glass-border)' }}
                          onClick={() => handleAssignAdmin(u.email)}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.15)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>{u.name || u.email.split('@')[0]}</div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{u.email} {u.rollNo ? `• ${u.rollNo}` : ''} {u.department ? `• ${u.department}` : ''}</div>
                          </div>
                          <UserPlus size={16} style={{ color: 'var(--ev-purple)' }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {assignMessage && <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: assignMessage.startsWith('✅') ? '#22c55e' : '#ef4444' }}>{assignMessage}</p>}
              </div>
            </div>
          )}

          {/* TAB 2: CONNECTIONS (PEOPLE-TO-PEOPLE DIRECTORY) */}
          {activeTab === 'connections' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.5rem' }}>People-to-People Connections</h2>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    Live list of attendee connections made during {eventData?.name}
                  </p>
                </div>
                {/* Filter Pills */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    onClick={() => setConnectionsStatusFilter('all')}
                    className={`ev-pill ${connectionsStatusFilter === 'all' ? 'selected' : ''}`}
                    style={{ fontSize: '0.82rem' }}
                  >
                    All ({totalAcceptedConns + totalPendingConns})
                  </button>
                  <button 
                    onClick={() => setConnectionsStatusFilter('accepted')}
                    className={`ev-pill ${connectionsStatusFilter === 'accepted' ? 'selected' : ''}`}
                    style={{ fontSize: '0.82rem', borderColor: connectionsStatusFilter === 'accepted' ? '#22c55e' : undefined, color: connectionsStatusFilter === 'accepted' ? '#22c55e' : undefined }}
                  >
                    Accepted ({totalAcceptedConns})
                  </button>
                  <button 
                    onClick={() => setConnectionsStatusFilter('pending')}
                    className={`ev-pill ${connectionsStatusFilter === 'pending' ? 'selected' : ''}`}
                    style={{ fontSize: '0.82rem', borderColor: connectionsStatusFilter === 'pending' ? 'var(--ev-orange)' : undefined, color: connectionsStatusFilter === 'pending' ? 'var(--ev-orange)' : undefined }}
                  >
                    Pending ({totalPendingConns})
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="ev-search-wrapper" style={{ marginBottom: '1.5rem' }}>
                <Search className="ev-search-icon" size={18} />
                <input 
                  className="ev-search-input" 
                  placeholder="Search by participant name, roll number, or email..." 
                  value={connectionsSearch} 
                  onChange={e => setConnectionsSearch(e.target.value)} 
                />
              </div>

              {/* Connections List */}
              {loadingConnections ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <Loader size={28} className="ev-spin" style={{ color: 'var(--ev-purple)' }} />
                </div>
              ) : connections.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--glass-bg)', borderRadius: 16, border: '1px solid var(--glass-border)', color: 'var(--text-muted)' }}>
                  No connections found{connectionsSearch ? ` matching "${connectionsSearch}"` : ''}.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                  {connections.map((conn) => (
                    <div 
                      key={conn.id} 
                      style={{ 
                        background: 'var(--glass-bg)', 
                        border: '1px solid var(--glass-border)', 
                        borderRadius: 16, 
                        padding: '1.25rem',
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr',
                        alignItems: 'center',
                        gap: '1.5rem'
                      }}
                    >
                      {/* Requester */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', minWidth: 0 }}>
                        <div className="ev-avatar ev-avatar-sm ev-avatar-gradient-0">
                          {conn.requester.profile_pic ? <img src={conn.requester.profile_pic} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}}/> : conn.requester.name?.charAt(0)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {conn.requester.name}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {[conn.requester.rollNo, conn.requester.department, conn.requester.year].filter(Boolean).join(' • ')}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{conn.requester.email}</div>
                        </div>
                      </div>

                      {/* Connection Middle Arrow & Status */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', textAlign: 'center' }}>
                        <span 
                          style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: 700, 
                            padding: '0.25rem 0.75rem', 
                            borderRadius: 12, 
                            background: conn.status === 'accepted' ? 'rgba(34,197,94,0.15)' : 'rgba(249,115,22,0.15)',
                            color: conn.status === 'accepted' ? '#22c55e' : '#f97316',
                            border: `1px solid ${conn.status === 'accepted' ? 'rgba(34,197,94,0.3)' : 'rgba(249,115,22,0.3)'}`
                          }}
                        >
                          {conn.status === 'accepted' ? '🤝 Connected' : '⏳ Pending Request'}
                        </span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {formatDate(conn.updated_at || conn.created_at)}
                        </span>
                      </div>

                      {/* Receiver */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', minWidth: 0, justifyContent: 'flex-end', textAlign: 'right' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {conn.receiver.name}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {[conn.receiver.rollNo, conn.receiver.department, conn.receiver.year].filter(Boolean).join(' • ')}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{conn.receiver.email}</div>
                        </div>
                        <div className="ev-avatar ev-avatar-sm ev-avatar-gradient-1">
                          {conn.receiver.profile_pic ? <img src={conn.receiver.profile_pic} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}}/> : conn.receiver.name?.charAt(0)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: SUB-EVENTS MANAGEMENT */}
          {activeTab === 'subevents' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Manage Sub-Events & Activities</h2>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    Add competitions, hackathons, or workshops for attendees to select
                  </p>
                </div>
                <button className="ev-btn-primary" onClick={() => setShowCreateActModal(true)}>
                  <Plus size={16} /> Add New Sub-Event
                </button>
              </div>

              {activities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--glass-bg)', borderRadius: 16, border: '1px solid var(--glass-border)', color: 'var(--text-muted)' }}>
                  <p style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>No sub-events configured yet for this event.</p>
                  <button className="ev-btn-primary" onClick={() => setShowCreateActModal(true)}>
                    <Plus size={16} /> Create Sub-Event
                  </button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                  {activities.map(act => (
                    <div key={act.id} className="match-card" style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.15rem' }}>{act.name}</h3>
                        <span className={`activity-badge ${(act.category || '').toLowerCase().replace(/\s+/g, '')}`}>
                          {act.category}
                        </span>
                      </div>

                      {act.description && (
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.4rem 0 1rem 0', lineHeight: 1.4 }}>
                          {act.description}
                        </p>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '1.25rem', marginTop: 'auto' }}>
                        {act.venue && <div>📍 Venue: <b>{act.venue}</b></div>}
                        {(act.date || act.start_time) && <div>⏰ Schedule: <b>{act.date ? `${act.date} ` : ''}{act.start_time}</b></div>}
                        {act.team_size && <div>👥 Team Size: <b>{act.team_size} members</b></div>}
                        <div style={{ color: 'var(--ev-purple)', fontWeight: 700 }}>
                          🎯 {act.participant_count || 0} Registered Attendees
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.75rem', borderTop: '1px solid var(--glass-border)' }}>
                        <button 
                          className="ev-btn-secondary" 
                          style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}
                          onClick={() => handleDeleteActivity(act.id, act.name)}
                        >
                          <Trash2 size={14} /> Delete Activity
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: PARTICIPANTS DIRECTORY */}
          {activeTab === 'participants' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Joined Participants Directory</h2>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    Total {participantTotal} attendees joined via event link
                  </p>
                </div>
              </div>

              <div className="ev-search-wrapper" style={{ marginBottom: '1.5rem' }}>
                <Search className="ev-search-icon" size={18} />
                <input 
                  className="ev-search-input" 
                  placeholder="Search participants by name, email, or roll number..." 
                  value={participantSearch} 
                  onChange={e => setParticipantSearch(e.target.value)} 
                />
              </div>

              {loadingParticipants ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <Loader size={28} className="ev-spin" style={{ color: 'var(--ev-purple)' }} />
                </div>
              ) : participants.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--glass-bg)', borderRadius: 16, border: '1px solid var(--glass-border)', color: 'var(--text-muted)' }}>
                  No participants found{participantSearch ? ` matching "${participantSearch}"` : ''}.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {participants.map((p, i) => (
                    <div key={p.email || i} style={{ padding: '0.85rem 1.25rem', background: 'var(--glass-bg)', borderRadius: 12, border: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.name || 'Unnamed'}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          {p.email} {p.rollNo ? `• ${p.rollNo}` : ''} {p.department ? `• ${p.department}` : ''} {p.year ? `(${p.year})` : ''}
                        </div>
                      </div>
                      {p.school_name && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--ev-purple)', background: 'rgba(139,92,246,0.12)', padding: '0.25rem 0.7rem', borderRadius: 10, fontWeight: 600 }}>
                          🎓 {p.school_name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 5: ANNOUNCEMENTS */}
          {activeTab === 'announcements' && (
            <div style={{ maxWidth: 640 }}>
              <h2 style={{ marginTop: 0, fontSize: '1.5rem' }}>Broadcast Push Announcement</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
                Sends an instant push notification and in-app alert to all attendees of {eventData?.name}.
              </p>
              <div className="ev-form-group">
                <label className="ev-label">Announcement Title</label>
                <input className="ev-input" placeholder="e.g. Hackathon Problem Statements are Live!" value={announcementTitle} onChange={e => setAnnouncementTitle(e.target.value)} />
              </div>
              <div className="ev-form-group">
                <label className="ev-label">Message Content</label>
                <textarea className="ev-input" placeholder="Enter message details for participants..." rows={4} value={announcementMessage} onChange={e => setAnnouncementMessage(e.target.value)}></textarea>
              </div>
              <button className="ev-btn-primary" onClick={handleSendAnnouncement} disabled={sendingAnnouncement || !announcementTitle.trim() || !announcementMessage.trim()}>
                {sendingAnnouncement ? 'Sending Broadcast...' : '🚀 Send Push Notification'}
              </button>
            </div>
          )}

        </div>
      </div>

      {/* CREATE SUB-EVENT MODAL */}
      {showCreateActModal && (
        <div className="ev-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowCreateActModal(false); }}>
          <div className="ev-modal" style={{ maxWidth: 520 }}>
            <div className="ev-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800 }}>Add Sub-Event / Activity</h2>
              <button onClick={() => setShowCreateActModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <div className="ev-modal-body">
              <div className="ev-form-group">
                <label className="ev-label">Sub-Event Name *</label>
                <input className="ev-input" placeholder="e.g. AI Hackathon, Cyber CTF, Web Sprint" value={newAct.name} onChange={e => setNewAct({ ...newAct, name: e.target.value })} />
              </div>

              <div className="ev-form-row">
                <div className="ev-form-group">
                  <label className="ev-label">Category</label>
                  <select className="ev-input ev-select" value={newAct.category} onChange={e => setNewAct({ ...newAct, category: e.target.value })}>
                    <option value="Hackathon">Hackathon</option>
                    <option value="CTF">CTF</option>
                    <option value="Contest">Contest</option>
                    <option value="Workshop">Workshop</option>
                    <option value="Paper Presentation">Paper Presentation</option>
                    <option value="Gaming">Gaming / E-Sports</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="ev-form-group">
                  <label className="ev-label">Team Size</label>
                  <input type="number" min="1" max="10" className="ev-input" value={newAct.team_size} onChange={e => setNewAct({ ...newAct, team_size: parseInt(e.target.value) || 1 })} />
                </div>
              </div>

              <div className="ev-form-group">
                <label className="ev-label">Description & Rules</label>
                <textarea className="ev-input" placeholder="Brief details about the sub-event..." rows={2} value={newAct.description} onChange={e => setNewAct({ ...newAct, description: e.target.value })} />
              </div>

              <div className="ev-form-row">
                <div className="ev-form-group">
                  <label className="ev-label">Date</label>
                  <input type="date" className="ev-input" value={newAct.date} onChange={e => setNewAct({ ...newAct, date: e.target.value })} />
                </div>
                <div className="ev-form-group">
                  <label className="ev-label">Time</label>
                  <input type="time" className="ev-input" value={newAct.start_time} onChange={e => setNewAct({ ...newAct, start_time: e.target.value })} />
                </div>
              </div>

              <div className="ev-form-group">
                <label className="ev-label">Venue</label>
                <input className="ev-input" placeholder="e.g. B-Block Labs, Seminar Hall 2" value={newAct.venue} onChange={e => setNewAct({ ...newAct, venue: e.target.value })} />
              </div>
            </div>
            <div className="ev-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button className="ev-btn-secondary" onClick={() => setShowCreateActModal(false)} disabled={creatingAct}>
                Cancel
              </button>
              <button className="ev-btn-primary" onClick={handleCreateActivity} disabled={creatingAct || !newAct.name.trim()}>
                {creatingAct ? 'Creating...' : 'Create Sub-Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT EVENT DETAILS & BANNER MODAL */}
      {showEditEventModal && (
        <div className="ev-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowEditEventModal(false); }}>
          <div className="ev-modal" style={{ maxWidth: 540 }}>
            <div className="ev-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800 }}>Edit Event Details & Cover Image</h2>
              <button onClick={() => setShowEditEventModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <div className="ev-modal-body">
              <div className="ev-form-group">
                <label className="ev-label">Event Name *</label>
                <input className="ev-input" value={editEventForm.name} onChange={e => setEditEventForm({ ...editEventForm, name: e.target.value })} />
              </div>

              {/* Cover Image URL / File Upload */}
              <div className="ev-form-group">
                <label className="ev-label">Cover Image / Banner</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input 
                    className="ev-input" 
                    placeholder="Paste image URL (https://...)" 
                    value={editEventForm.banner?.startsWith('data:') ? '' : editEventForm.banner} 
                    onChange={e => setEditEventForm({ ...editEventForm, banner: e.target.value })} 
                    style={{ flex: 1 }}
                  />
                  <label className="ev-btn-secondary" style={{ cursor: 'pointer', padding: '0.6rem 1rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <ImageIcon size={16} /> Upload
                    <input type="file" accept="image/*" onChange={handleEditBannerFile} style={{ display: 'none' }} />
                  </label>
                </div>
                {editEventForm.banner && (
                  <div style={{ position: 'relative', height: '120px', borderRadius: 12, overflow: 'hidden', marginTop: '0.5rem', border: '1px solid var(--glass-border)' }}>
                    <img src={editEventForm.banner} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button 
                      onClick={() => setEditEventForm({ ...editEventForm, banner: '' })}
                      style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', color: '#fff', width: 24, height: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>

              <div className="ev-form-group">
                <label className="ev-label">Description</label>
                <textarea className="ev-input" rows={3} value={editEventForm.description} onChange={e => setEditEventForm({ ...editEventForm, description: e.target.value })} />
              </div>

              <div className="ev-form-row">
                <div className="ev-form-group">
                  <label className="ev-label">Start Date</label>
                  <input type="date" className="ev-input" value={editEventForm.start_date} onChange={e => setEditEventForm({ ...editEventForm, start_date: e.target.value })} />
                </div>
                <div className="ev-form-group">
                  <label className="ev-label">End Date</label>
                  <input type="date" className="ev-input" value={editEventForm.end_date} onChange={e => setEditEventForm({ ...editEventForm, end_date: e.target.value })} />
                </div>
              </div>

              <div className="ev-form-group">
                <label className="ev-label">Venue</label>
                <input className="ev-input" value={editEventForm.venue} onChange={e => setEditEventForm({ ...editEventForm, venue: e.target.value })} />
              </div>
            </div>
            <div className="ev-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button className="ev-btn-secondary" onClick={() => setShowEditEventModal(false)} disabled={savingEvent}>
                Cancel
              </button>
              <button className="ev-btn-primary" onClick={handleSaveEventDetails} disabled={savingEvent || !editEventForm.name.trim()}>
                {savingEvent ? 'Saving Changes...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
